import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PrivacySidebar from './components/PrivacySidebar';
import DriversPrivacySection from './components/DriversPrivacySection';
import VehiclesPrivacySection from './components/VehiclesPrivacySection';
import PrivacyHistoryTable from './components/PrivacyHistoryTable';
import DeleteDataForm from './components/DeleteDataForm';
import DeletionHistoryTable from './components/DeletionHistoryTable';
import styles from './PrivacyTab.module.scss';

const SECTION_IDS = [
    'sec-drivers-privacy',
    'sec-drivers-history',
    'sec-anonymize-driver',
    'sec-anonymize-history',
    'sec-vehicles-privacy',
    'sec-vehicles-history',
    'sec-delete-vehicle',
    'sec-delete-history',
];

const PrivacyTab: React.FC = () => {
    const { t } = useTranslation();
    const contentRef = useRef<HTMLDivElement>(null);
    const [activeSectionId, setActiveSectionId] = useState(SECTION_IDS[0]);
    const [deletionRefreshKey, setDeletionRefreshKey] = useState(0);
    const [historyRefreshKey, setHistoryRefreshKey] = useState(0);

    const handleScroll = useCallback(() => {
        const container = contentRef.current;
        if (!container) return;

        const containerRect = container.getBoundingClientRect();
        let active = SECTION_IDS[0];

        for (const id of SECTION_IDS) {
            const el = document.getElementById(id);
            if (!el) continue;
            const elRect = el.getBoundingClientRect();
            const relativeTop = elRect.top - containerRect.top;
            if (relativeTop <= 100) {
                active = id;
            }
        }

        setActiveSectionId(active);
    }, []);

    useEffect(() => {
        const container = contentRef.current;
        if (!container) return;

        container.addEventListener('scroll', handleScroll);
        handleScroll();

        return () => {
            container.removeEventListener('scroll', handleScroll);
        };
    }, [handleScroll]);

    const handleSectionClick = (sectionId: string) => {
        const container = contentRef.current;
        const el = document.getElementById(sectionId);
        if (!container || !el) return;

        const offsetTop = el.offsetTop - container.offsetTop;
        container.scrollTo({ top: offsetTop - 20, behavior: 'smooth' });
    };

    const handleDeletionComplete = () => {
        setDeletionRefreshKey(prev => prev + 1);
    };

    return (
        <div className={styles.container}>
            <PrivacySidebar
                activeSectionId={activeSectionId}
                onSectionClick={handleSectionClick}
            />
            <div className={styles.content} ref={contentRef}>
                {/* RGPD Banner */}
                <div className={styles.rgpdBanner}>
                    <div className={styles.rgpdIcon}>&#128737;</div>
                    <div>
                        <div className={styles.rgpdTitle}>{t('privacy.rgpd_title') as string}</div>
                        <div className={styles.rgpdDesc}>{t('privacy.rgpd_desc') as string}</div>
                    </div>
                    <div className={styles.rgpdBadge}>RGPD</div>
                </div>

                {/* DRIVERS - Privacy */}
                <div className={styles.sectionTitle} id="sec-drivers-privacy">
                    {t('privacy.section_drivers') as string}
                </div>

                <DriversPrivacySection onToggle={() => setHistoryRefreshKey(k => k + 1)} />

                <div id="sec-drivers-history">
                    <PrivacyHistoryTable entityType="driver" refreshKey={historyRefreshKey} />
                </div>

                {/* DRIVERS - Anonymize */}
                <div className={styles.sectionAnchor} id="sec-anonymize-driver">
                    <DeleteDataForm entityType="driver" onDeleted={handleDeletionComplete} />
                </div>

                <div id="sec-anonymize-history">
                    <DeletionHistoryTable entityType="driver" refreshKey={deletionRefreshKey} />
                </div>

                {/* VEHICLES - Privacy */}
                <div className={styles.sectionTitle} id="sec-vehicles-privacy">
                    {t('privacy.section_assets') as string}
                </div>

                <VehiclesPrivacySection onToggle={() => setHistoryRefreshKey(k => k + 1)} />

                <div id="sec-vehicles-history">
                    <PrivacyHistoryTable entityType="vehicle" refreshKey={historyRefreshKey} />
                </div>

                {/* VEHICLES - Delete */}
                <div className={styles.sectionAnchor} id="sec-delete-vehicle">
                    <DeleteDataForm entityType="vehicle" onDeleted={handleDeletionComplete} />
                </div>

                <div id="sec-delete-history">
                    <DeletionHistoryTable entityType="vehicle" refreshKey={deletionRefreshKey} />
                </div>

                {/* Bottom spacer for scrollspy */}
                <div className={styles.bottomSpacer} />
            </div>
        </div>
    );
};

export default PrivacyTab;
